<template>
  <section class="shared-section">
    <header class="shared-section-header">
      <h2 class="pl-4 shared-section-header-heading sm:pl-0">Payout Tags</h2>
    </header>

    <!-- Table -->
    <Table
      toolbarHeading="Payout Tags List"
      :withToolbar="true"
      :withSearch="true"
      :withPagination="true"
      :tColumns="$options.tColumns"
      :tData="payoutTags"
      :fetch="fetchPayoutTags"
    >
      <template #table-toolbar>
        <button type="button" class="shared-table-toolbar-button" @click="showModal = true">
          <VSvgIcon name="plus" class="h-5 mr-1" /> Add Tag
        </button>
      </template>

      <template #table-data-1="trData">
        <div>
          <div
            v-for="(type, index) in trData['product types']"
            :key="`product-type-index-${index}`"
            class="py-2"
          >
            {{ type }}
          </div>
        </div>
      </template>

      <template #table-data-2="trData">
        <div>
          <div
            v-for="(amount, index) in trData['payout amounts']"
            :key="`payout-amounts-index-${index}`"
            class="py-2"
          >
            {{ amount }}
          </div>
        </div>
      </template>

      <template #table-data-actions="trData">
        <td class="shared-table-cells-cell">
          <div class="flex justify-center">
            <button
              class="mr-2 shared-table-cells-button round edit"
              @click="() => handleSelectEditableTag(trData)"
            >
              <VSvgIcon name="pencil" class="h-5" />
            </button>
            <button
              class="mr-2 shared-table-cells-button round"
              @click="() => handleSelectPartiallyFilledTag(trData)"
            >
              <VSvgIcon name="copy" class="h-5" />
            </button>
            <button
              class="shared-table-cells-button round delete"
              @click="handleDeleteTag(trData.id)"
            >
              <VSvgIcon name="trash" class="h-5" />
            </button>
          </div>
        </td>
      </template>
    </Table>

    <!-- Modals -->
    <ModalPlus
      :title="editable ? 'Edit Payout Tag' : 'Add Payout Tag'"
      :show="showModal"
      :onClose="handleClose"
      :withButtons="false"
      maxWidth="800px"
    >
      <div class="pt-8">
        <Tabs v-if="editable" :tabs="tabs" @change="t => (tab = t)" />
        <div>
          <PayoutTagForm
            v-if="tab.order === 0"
            :editable="editable"
            :editableTag="editableTag"
            :machineTypes="machineTypes"
            :initialValues="editable ? editableTag : partiallyFilledTag"
            @close="handleClose"
          />
          <PayoutTagProductsForm
            v-else-if="tab.order === 1"
            :products="products"
            :tagProducts="editableTagProducts"
            :tagId="editableTag.id"
            @filter="text => handleFilterProducts(text)"
            @update="products => (editableTagProducts = products)"
            @close="handleClose"
          />
        </div>
      </div>
    </ModalPlus>
  </section>
</template>

<script>
import { mapState } from 'vuex';

import { safeNullDataset } from '@/utils';

import VSvgIcon from '@/components/base/VSvgIcon';
import ModalPlus from '@/components/shared/modal/ModalPlus';
import Table from '@/components/shared/table/Table';
import PayoutTagForm from '@/components/admin/PayoutTagForm';
import PayoutTagProductsForm from '@/components/admin/PayoutTagProductsForm';
import Tabs from '@/components/shared/Tabs';

// a few helper functions
const attachSymbol = (type, value) => (type === 'currency' ? `$${value}` : `${value}%`);

const normarlizeProductTypesColumn = (type, data) => {
  const machineNames = data.map(d => d.name);
  return type === 'currency' ? machineNames : ['Entire order'];
};

const normalizePayoutAmountColumn = (type, data) => {
  const machineValues = data.map(d => attachSymbol(type, d.value));
  return type === 'currency' ? machineValues : [machineValues[0]];
};

const normalizeShopProductsRewardColumn = (type, data) => {
  return attachSymbol(type, data);
};

const TABS = [
  { name: 'Payout tags', order: 0 },
  { name: "Payout tags's Products", order: 1 },
];

export default {
  name: 'PayoutTags',
  components: { VSvgIcon, ModalPlus, Table, PayoutTagForm, PayoutTagProductsForm, Tabs },
  data() {
    return {
      showModal: false,
      editableTag: {},
      partiallyFilledTag: {},
      editableTagProducts: [],
      machineTypes: [],
      tab: TABS[0],
      products: [],
    };
  },
  watch: {
    // reset tabs back to Customer on open/close modal
    showModal() {
      this.tab = { name: 'Payout tags', order: 0 };
    },
  },
  computed: mapState({
    tabs() {
      return TABS;
    },
    payoutTags: state => ({
      data: safeNullDataset(state)(['admin', 'payoutTags', 'data']).map(tag => ({
        'payout tag name': tag.name || '-',
        'product types': normarlizeProductTypesColumn(tag.type, tag.machine_type),
        'payout amounts': normalizePayoutAmountColumn(tag.type, tag.machine_type),
        'shop products reward': normalizeShopProductsRewardColumn(
          tag.type,
          tag.shop_products_reward,
        ),
        'referral threshold': tag.minimum_referrals || '-',
        'required new lead': tag.required_new_lead ? 'YES' : 'NO',
        'embroidery required': tag.machine_required ? 'YES' : 'NO',
        ...tag,
      })),
      meta: state.admin.payoutTags.meta,
    }),
    editable() {
      return !!Object.keys(this.editableTag).length;
    },
  }),
  methods: {
    async handleFilterProducts(text) {
      const params = { search: text, per_page: 1000000, includes: [''] };
      const updated = await this.$store.dispatch('admin/getProducts', params);
      this.products = updated.data;
    },
    async fetchPayoutTags(params) {
      return await this.$store.dispatch('admin/getPayoutTags', params);
    },
    handleSelectPartiallyFilledTag(tag) {
      this.partiallyFilledTag = this.prepSelectedTagData(tag);
      this.showModal = true;
    },
    handleSelectEditableTag(tag) {
      this.editableTag = this.prepSelectedTagData(tag);
      this.handleSetEditableTagProducts(tag.id);
      this.showModal = true;
    },
    async handleSetEditableTagProducts(id) {
      const { data } = await this.$http.get(`/admin/referral-payout-tag-product/${id}`);
      this.editableTagProducts = data.data;
    },
    prepSelectedTagData(tag) {
      const {
        id,
        name: payout_tag_name,
        type: payout_type,
        machine_type,
        minimum_referrals,
        shop_products_reward,
        machine_required,
        required_new_lead,
        has_dtg_reward,
      } = tag;
      const payout_amounts = machine_type.map(({ id, value }) => ({ machine_type_id: id, value }));
      return {
        id,
        payout_tag_name,
        payout_type,
        payout_amounts,
        minimum_referrals,
        shop_products_reward,
        machine_required,
        required_new_lead,
        has_dtg_reward,
      };
    },
    handleClose() {
      this.partiallyFilledTag = {};
      this.editableTag = {};
      this.showModal = false;
    },
  },
  async mounted() {
    const { data } = await this.$http.get('/admin/machine-type');
    this.machineTypes = data.data;
  },
  tColumns: [],
  created() {
    this.$options.tColumns = [
      { column: 'payout tag name', isSort: true, orderBy: 'name' },
      { column: 'product types', isSort: false },
      { column: 'payout amounts', isSort: false },
      { column: 'shop products reward', isSort: false },
      { column: 'referral threshold', isSort: true, orderBy: 'minimum_referrals' },
      { column: 'required new lead', isSort: false },
      { column: 'embroidery required', isSort: false },
      { column: 'actions', isSort: false },
    ];
  },
};
</script>
